import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { connect } from "react-redux";
import { 
  getOrganizerAPI, 
  getOrganizerDiscountsAPI, 
  getAnalyticsAPI, 
  getUserNotificationsAPI, 
  updateOrganizerAPI, 
  deleteDiscountAPI, 
  markNotificationAsReadAPI,
  markAllNotificationsAsReadAPI,
  deleteNotificationAPI
} from "../../actions";
import NotificationList from "../User/NotificationList";
import Card from "../Shared/Card";
import TokensTab from "./TokensTab";

// ─── Theme tokens (light) ─────────────────────────────────────────────────────
const T = {
  bg: "#faf8f5",
  surface: "#ffffff",
  surfaceWarm: "#f5f2ed",
  surfaceHover: "rgba(0,0,0,0.025)",
  border: "rgba(0,0,0,0.08)",
  borderMed: "rgba(0,0,0,0.12)",
  orange: "#fa8128",
  orangeDim: "rgba(250,129,40,0.1)",
  orangeGlow: "rgba(250,129,40,0.06)",
  text: "#1a1710",
  textMuted: "rgba(26,23,16,0.42)",
  textSub: "rgba(26,23,16,0.6)",
  success: "#16a34a",
  successBg: "rgba(22,163,74,0.08)",
  error: "#dc2626",
  errorBg: "rgba(220,38,38,0.08)",
  radius: "12px",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

function Empty({ icon, title, body }) {
  return (
    <div className="dashboard-empty">
      <div className="dashboard-empty-icon">{icon}</div>
      <div className="dashboard-empty-title">{title}</div>
      <div className="dashboard-empty-body">{body}</div>
    </div>
  );
}

// ─── Page Shell ───────────────────────────────────────────────────────────────
const PageWrap = styled.div`
  min-height: 100vh;
  background-color: ${T.bg};
  background-image:
    radial-gradient(ellipse 70% 45% at 50% 0%, rgba(250,129,40,0.07) 0%, transparent 68%),
    radial-gradient(ellipse 40% 30% at 80% 80%, rgba(250,129,40,0.03) 0%, transparent 60%);
  padding: 80px 16px 120px;
  @media (max-width: 480px) { padding: 72px 12px 100px; }
`;

const Container = styled.div`
  max-width: 80%;
  margin: 0 auto;
  animation: ${fadeUp} 0.5s ease;
  @media (max-width: 480px) { max-width: 100%; }
`;

// ─── Stats ────────────────────────────────────────────────────────────────────
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 768px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px)  { grid-template-columns: repeat(2, 1fr); }
`;

const StatCard = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  padding: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  &::before {
    content: "";
    position: absolute;
    top: 0; right: 0;
    width: 60px; height: 60px;
    background: radial-gradient(circle, ${T.orangeGlow} 0%, transparent 70%);
  }
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${T.orangeGlow};
  border: 1px solid ${T.orangeDim};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${T.orange};
  font-size: 16px;
`;

const StatValue = styled.div`
  font-family: "Georgia", serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ color }) => color || T.text};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-family: "Courier New", monospace;
  font-size: 9px;
  letter-spacing: 0.1em;
  color: ${T.text};
  text-transform: uppercase;
`;

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  padding: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  &::-webkit-scrollbar { display: none; }
`;

const TabBtn = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: "Courier New", monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${({ active }) => active ? T.orange : 'transparent'};
  border: none;
  color: ${({ active }) => active ? '#fff' : "#5b5b5b"};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover {
    background: ${({ active }) => active ? T.orange : T.surfaceWarm};
    color: ${({ active }) => active ? '#fff' : T.text};
  }
`;

const TabBadge = styled.span`
  background: ${({ active }) => active ? 'rgba(255,255,255,0.25)' : T.orangeDim};
  color: ${({ active }) => active ? '#fff' : T.orange};
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 700;
`;

// ─── Section Card ─────────────────────────────────────────────────────────────
const Section = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${T.border};
`;

const SectionTitle = styled.h2`
  font-family: "Georgia", serif;
  font-size: 1rem;
  font-weight: 600;
  color: ${T.text};
  margin: 0;
`;

// ─── Discount List ────────────────────────────────────────────────────────────
const DiscountList = styled.div`padding: 8px;`;

const DiscountItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 8px;
  transition: background 0.2s;
  cursor: pointer;
  &:hover { background: ${T.surfaceWarm}; }
`;

const DiscountIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${T.orangeGlow};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const DiscountInfo = styled.div`flex: 1; min-width: 0;`;

const DiscountTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${T.text};
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DiscountMeta = styled.div`
  font-size: 11px;
  color: ${T.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${T.textMuted};
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 13px;
  margin: 0;
`;

// ─── Card Grid ────────────────────────────────────────────────────────────────
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding: 8px;
`;

// ─── Notification Filter Bar ──────────────────────────────────
const NOTIFICATION_TYPES = [
  { value: "", label: "All Types" },
  { value: "order_status", label: "Order Status" },
  { value: "discount_update", label: "Discount Update" },
  { value: "new_follower", label: "New Follower" },
  { value: "review_posted", label: "Review Posted" },
  { value: "discount_published", label: "Discount Published" },
  { value: "payment_received", label: "Payment Received" },
  { value: "ticket_purchased", label: "Ticket Purchased" },
];

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid ${T.border};
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${T.border};
  background: ${T.surface};
  font-size: 13px;
  color: ${T.text};
  cursor: pointer;
  outline: none;
  min-width: 150px;

  &:focus {
    border-color: ${T.orange};
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 4px;
  background: ${T.surfaceWarm};
  padding: 3px;
  border-radius: 8px;
`;

const ToggleBtn = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? T.orange : T.textSub)};
  box-shadow: ${({ $active }) => ($active ? "0 1px 3px rgba(0,0,0,0.08)" : "none")};
  transition: all 0.15s;

  &:hover {
    color: ${T.text};
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────
const OrganizerDashboard = (props) => {
  const navigate = useNavigate();

  const handleEdit = (discountId) => {
    navigate(`/discounts/update/${discountId}`);
  };
  const {
    getOrganizer,
    organizer,
    getOrganizerDiscounts,
    getAnalytics,
    notifications,
    getUserNotifications,
    discounts: discountsRedux,
    analytics: analyticsRedux
  } = props;

  const discountsFlat = discountsRedux?.results || discountsRedux || []; // Ensure flat array for UpdateDiscount

  const [tab, setTab] = useState("all");
  const [filterType, setFilterType] = useState("");
  const [filterRead, setFilterRead] = useState("unread"); // "all" | "unread" | "read"

  const [orgName, setOrgName] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [orgLocation, setOrgLocation] = useState("");
  const [saveState, setSaveState] = useState(null);

  useEffect(() => {
    if (organizer) {
      setOrgName(organizer.name || "");
      setOrgEmail(organizer.email || "");
      setOrgPhone(organizer.phone_number || "");
      setOrgDescription(organizer.description || "");
      setOrgLocation(organizer.location || "");
    }
  }, [organizer]);

  const handleSaveOrganizer = (e) => {
    e.preventDefault();
    setSaveState("saving");
    const fd = new FormData();
    fd.append("name", orgName);
    fd.append("email", orgEmail);
    fd.append("phone_number", orgPhone);
    fd.append("description", orgDescription);
    fd.append("location", orgLocation);
    props.updateOrganizer(fd);
    setTimeout(() => {
      setSaveState("saved");
      setTimeout(() => setSaveState(null), 3000);
    }, 900);
  };

  useEffect(() => { getOrganizer(); }, [getOrganizer]);

  useEffect(() => {
    if (organizer && organizer.id) {
      getOrganizerDiscounts(organizer.id);
      getAnalytics(organizer.id);
    }
  }, [organizer, getOrganizerDiscounts, getAnalytics]);

  useEffect(() => {
    const filters = {};
    if (filterType) filters.type = filterType;
    if (filterRead === "unread") filters.is_read = false;
    else if (filterRead === "read") filters.is_read = true;
    getUserNotifications(filters);
  }, [filterType, filterRead, getUserNotifications]);

  const discountsData = discountsRedux?.results || discountsRedux || [];
  const analytics = analyticsRedux || {};
  const discountSummary = analytics.discount_summary || {};
  const engagement = analytics.engagement || {};
  const organizerInfo = analytics.organizer || {};

  const activeDiscounts = discountSummary.active_discounts !== undefined
    ? discountSummary.active_discounts
    : discountsData.filter(d => d.status === 'active').length;
  const pendingDiscounts = discountSummary.pending_discounts !== undefined
    ? discountSummary.pending_discounts
    : discountsData.filter(d => d.status === 'pending').length;
  const rejectedDiscounts = discountSummary.rejected_discounts !== undefined
    ? discountSummary.rejected_discounts
    : discountsData.filter(d => d.status === 'rejected').length;

  const totalLikes = engagement.total_likes || 0;
  const totalReviews = engagement.total_reviews || 0;
  const averageRating = engagement.average_rating || 0;
  const totalWishlists = engagement.total_wishlists || 0;
  const followersCount = organizerInfo.followers_count || 0;

  const isExpiredDiscount = (discount) => {
    if (discount.status === 'expired') return true;
    if (discount.end_date) return new Date(discount.end_date) < new Date();
    return false;
  };

  const expiredDiscountsCount = discountSummary.expired_discounts !== undefined
    ? discountSummary.expired_discounts
    : discountsData.filter(d => isExpiredDiscount(d)).length;

  const stats = {
    totalDiscounts: discountSummary.total_discounts !== undefined
      ? discountSummary.total_discounts
      : discountsData.length,
    activeDiscounts,
    pendingDiscounts,
    rejectedDiscounts,
    expiredDiscounts: expiredDiscountsCount,
    totalLikes,
    totalReviews,
    averageRating,
    totalWishlists,
    followersCount,
  };

  const notificationsData = props.notifications.results || [];
  const unreadCount = notificationsData.filter(n => !n.is_read).length;

  const tabs = [
    { id: "all", label: "My Discounts", badge: stats.totalDiscounts },
    { id: "notifications", label: "Notifications", badge: unreadCount > 0 ? unreadCount : null },
    { id: "tokens", label: "Tokens", badge: stats.activeDiscounts },
    { id: "active", label: "Active" },
    { id: "pending", label: "Pending", badge: stats.pendingDiscounts },
    { id: "rejected", label: "Rejected", badge: stats.rejectedDiscounts },
    { id: "expired", label: "Expired", badge: stats.expiredDiscounts },
    { id: "settings", label: "Settings" },
  ];

  const filteredDiscounts = tab === "all"
    ? discountsData
    : tab === "expired"
      ? discountsData.filter(d => isExpiredDiscount(d))
      : discountsData.filter(d => d.status === tab);

  // Settings form input style (inline — matches existing code pattern)
  const inputStyle = {
    width: '100%',
    padding: '12px',
    background: T.surface,
    border: `1px solid ${T.borderMed}`,
    borderRadius: '8px',
    color: T.text,
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    color: T.text,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <PageWrap>
      <Container>
        {/* Stats */}
        <StatsGrid>
          <StatCard><StatIcon>🏷️</StatIcon><StatValue>{stats.totalDiscounts}</StatValue><StatLabel>Total</StatLabel></StatCard>
          <StatCard><StatIcon>✓</StatIcon><StatValue color={T.success}>{stats.activeDiscounts}</StatValue><StatLabel>Active</StatLabel></StatCard>
          <StatCard><StatIcon>⏳</StatIcon><StatValue color={T.orange}>{stats.pendingDiscounts}</StatValue><StatLabel>Pending</StatLabel></StatCard>
          <StatCard><StatIcon>✕</StatIcon><StatValue color={T.error}>{stats.rejectedDiscounts}</StatValue><StatLabel>Rejected</StatLabel></StatCard>
          <StatCard><StatIcon>📅</StatIcon><StatValue>{stats.expiredDiscounts}</StatValue><StatLabel>Expired</StatLabel></StatCard>
          <StatCard><StatIcon>👥</StatIcon><StatValue>{stats.followersCount}</StatValue><StatLabel>Followers</StatLabel></StatCard>
          <StatCard><StatIcon>❤️</StatIcon><StatValue>{stats.totalLikes}</StatValue><StatLabel>Likes</StatLabel></StatCard>
          <StatCard><StatIcon>⭐</StatIcon><StatValue>{stats.averageRating > 0 ? stats.averageRating.toFixed(1) : 'N/A'}</StatValue><StatLabel>Avg Rating</StatLabel></StatCard>
          <StatCard><StatIcon>📝</StatIcon><StatValue>{stats.totalReviews}</StatValue><StatLabel>Reviews</StatLabel></StatCard>
          <StatCard><StatIcon>💝</StatIcon><StatValue>{stats.totalWishlists}</StatValue><StatLabel>Wishlists</StatLabel></StatCard>
        </StatsGrid>

        {/* Tabs */}
        <TabBar>
          {tabs.map((t) => (
            <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
              {t.badge ? <TabBadge active={tab === t.id}>{t.badge}</TabBadge> : null}
            </TabBtn>
          ))}
        </TabBar>

        {/* Tab Content */}
        {tab === "notifications" ? (
          <Section>
            <SectionHeader>
              <SectionTitle>Notifications</SectionTitle>
              {unreadCount > 0 && <TabBadge active={false}>{unreadCount} unread</TabBadge>}
            </SectionHeader>
            <FilterBar>
              <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                {NOTIFICATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </FilterSelect>
              <ToggleGroup>
                <ToggleBtn $active={filterRead === "all"} onClick={() => setFilterRead("all")}>All</ToggleBtn>
                <ToggleBtn $active={filterRead === "unread"} onClick={() => setFilterRead("unread")}>Unread</ToggleBtn>
                <ToggleBtn $active={filterRead === "read"} onClick={() => setFilterRead("read")}>Read</ToggleBtn>
              </ToggleGroup>
            </FilterBar>
            {tab === "notifications" && (
              !notifications || notifications.results?.length === 0
                ? <Empty icon="🔔" title="All caught up" body="No notifications right now." />
                : <NotificationList
                  notifications={notifications}
                  onMarkRead={props.markNotificationAsRead}
                  onMarkAllRead={props.markAllNotificationsAsRead}
                  onDelete={props.deleteNotification}
                />
            )}
          </Section>
        ) : tab === "tokens" ? (
          <Section>
            <SectionHeader>
              <SectionTitle>Token Verification & Redemption</SectionTitle>
            </SectionHeader>
            <TokensTab />
          </Section>
        ) : tab === "settings" ? (
          <Section>
            <SectionHeader>
              <SectionTitle>Settings</SectionTitle>
            </SectionHeader>
            <form style={{ padding: '20px' }} onSubmit={handleSaveOrganizer}>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Organizer Name</label>
                <input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Contact Email</label>
                <input
                  value={orgEmail}
                  onChange={(e) => setOrgEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Phone Number</label>
                <input
                  value={orgPhone}
                  onChange={(e) => setOrgPhone(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Location</label>
                <input
                  value={orgLocation}
                  onChange={(e) => setOrgLocation(e.target.value)}
                  placeholder="e.g. Accra, Ghana"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Bio / Description</label>
                <textarea
                  value={orgDescription}
                  onChange={(e) => setOrgDescription(e.target.value)}
                  placeholder="Tell customers about your business…"
                  rows={4}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    lineHeight: '1.6',
                    minHeight: '96px',
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={saveState === "saving"}
                style={{
                  padding: '12px 24px',
                  background: T.orange,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saveState === "saving" ? 'not-allowed' : 'pointer',
                  opacity: saveState === "saving" ? 0.7 : 1,
                }}
              >
                {saveState === "saving" ? "Saving..." : saveState === "saved" ? "✓ Saved!" : "Save Changes"}
              </button>
            </form>
          </Section>
        ) : (
          <Section>
            <SectionHeader>
              <SectionTitle>
                {tab === "all" ? "All Discounts" : tab === "expired" ? "Expired Discounts" : tab.charAt(0).toUpperCase() + tab.slice(1) + " Discounts"}
              </SectionTitle>
            </SectionHeader>
            {filteredDiscounts.length > 0 ? (
              <CardGrid>
                {filteredDiscounts.map((discount) => (
                  <Card
                    key={discount.id}
                    discount={discount}
                    bgColor="light"
                    isEditMode={true}
                    onEdit={handleEdit}
                    onDelete={(id) => {
                      if (window.confirm("Are you sure you want to delete this discount?")) {
                        props.deleteDiscount(id);
                      }
                    }}
                  />
                ))}
              </CardGrid>
            ) : (
              <EmptyState>
                <EmptyIcon>🏷️</EmptyIcon>
                <EmptyText>No {tab !== "all" ? tab : ""} discounts yet.</EmptyText>
              </EmptyState>
            )}
          </Section>
        )}
      </Container>
    </PageWrap>
  );
};

// ─── Redux ────────────────────────────────────────────────────────────────────
const mapStateToProps = (state) => ({
  organizer: state.organizerState.organizer,
  discounts: state.organizerState.discounts,
  analytics: state.organizerState.analytics,
  notifications: state.userState.notifications,
});

const mapDispatchToProps = (dispatch) => ({
  getOrganizer: () => dispatch(getOrganizerAPI()),
  getOrganizerDiscounts: (organizer_id) => dispatch(getOrganizerDiscountsAPI(organizer_id)),
  getAnalytics: (organizer_id) => dispatch(getAnalyticsAPI(organizer_id)),
  getUserNotifications: (filters) => dispatch(getUserNotificationsAPI(filters)),
  updateOrganizer: (data) => dispatch(updateOrganizerAPI(data)),
  deleteDiscount: (discount_id) => dispatch(deleteDiscountAPI(discount_id)),
  markNotificationAsRead: (id) => dispatch(markNotificationAsReadAPI(id)),
  markAllNotificationsAsRead: () => dispatch(markAllNotificationsAsReadAPI()),
  deleteNotification: (id) => dispatch(deleteNotificationAPI(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganizerDashboard);